import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { guardarDireccion, cargarDatosEmpresa, actualizarZonaHoraria } from '../../actions/auth';
import { ProgressBar } from '../shared/ProgressBar';
import { getLadaPorZona } from '../../helpers/util';
import Swal from 'sweetalert2';

const ZONAS_POR_PAIS = {
    'MX': 'America/Mexico_City',
    'CO': 'America/Bogota',
    'PE': 'America/Lima',
    'CL': 'America/Santiago',
    'AR': 'America/Argentina/Buenos_Aires',
    'BR': 'America/Sao_Paulo',
    'VE': 'America/Caracas',
    'EC': 'America/Guayaquil',
    'BO': 'America/La_Paz',
    'PY': 'America/Asuncion',
    'UY': 'America/Montevideo',
    'PA': 'America/Panama',
    'CR': 'America/Costa_Rica',
    'GT': 'America/Guatemala',
    'HN': 'America/Tegucigalpa',
    'NI': 'America/Managua',
    'SV': 'America/El_Salvador',
    'DO': 'America/Santo_Domingo',
    'CU': 'America/Havana',
    'PR': 'America/Puerto_Rico',
};

export const PasoDireccion = ({ siguiente, anterior }) => {
    const dispatch = useDispatch();
    const { zonaHoraria, hashtag } = useSelector(state => state.auth);
    const lada = getLadaPorZona(zonaHoraria);
    const bandera = lada === '521' ? '🇲🇽' : lada === '57' ? '🇨🇴' : lada === '51' ? '🇵🇪' :
        lada === '56' ? '🇨🇱' : lada === '54' ? '🇦🇷' : lada === '55' ? '🇧🇷' : '🌎';

    const [direccion, setDireccion] = useState('');
    const [mapsLink, setMapsLink] = useState('');
    const [lugarSeleccionado, setLugarSeleccionado] = useState(false);
    const [telefonos, setTelefonos] = useState(['']);
    const [tolerancia, setTolerancia] = useState(false);
    const [minutos, setMinutos] = useState('10');
    const [cargando, setCargando] = useState(false);
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);

    // Cargar datos existentes al entrar al paso
    useEffect(() => {
        const cargar = async () => {
            const data = await dispatch(cargarDatosEmpresa());
            if (data) {
                if (data.direccion) setDireccion(data.direccion);
                if (data.maps) setMapsLink(data.maps);
                if (data.tolerancia && data.tolerancia !== '-1') {
                    setTolerancia(true);
                    setMinutos(data.tolerancia);
                }
                if (data.telefonos_contacto && data.telefonos_contacto.length > 0) {
                    const tels = data.telefonos_contacto.map(t => t.slice(-10));
                    setTelefonos(tels);
                }
            }
        };
        cargar();
    }, [hashtag]);

    useEffect(() => {
        if (!window.google || !inputRef.current) return;
        try {
            autocompleteRef.current = new window.google.maps.places.Autocomplete(
                inputRef.current,
                { types: ['establishment', 'geocode'] }
            );
            autocompleteRef.current.addListener('place_changed', () => {
                const place = autocompleteRef.current.getPlace();
                if (place.formatted_address || place.name) {
                    const addr = place.formatted_address || place.name;
                    setDireccion(addr);
                    setLugarSeleccionado(true);
                    if (place.url) {
                        setMapsLink(place.url);
                    } else if (place.geometry) {
                        const lat = place.geometry.location.lat();
                        const lng = place.geometry.location.lng();
                        setMapsLink(`https://www.google.com/maps?q=${lat},${lng}`);
                    }
                    // Detectar zona horaria exacta por coordenadas (Timezone API)
                    if (place.geometry) {
                        const lat = place.geometry.location.lat();
                        const lng = place.geometry.location.lng();
                        const timestamp = Math.floor(Date.now() / 1000);
                        fetch(`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`)
                            .then(r => r.json())
                            .then(tz => {
                                if (tz.timeZoneId && tz.timeZoneId !== zonaHoraria) {
                                    dispatch(actualizarZonaHoraria(tz.timeZoneId, getLadaPorZona(tz.timeZoneId)));
                                    // Avisar si ya tenían calendarios configurados
                                    import('../../firebase/firebase-config').then(({ db }) => {
                                        db.collection(`general/${hashtag}/consultores`).get().then(snap => {
                                            if (!snap.empty) {
                                                const { default: Swal } = require('sweetalert2');
                                                Swal.fire({
                                                    icon: 'info',
                                                    title: 'Zona horaria actualizada',
                                                    html: `Tu dirección está en una zona horaria diferente (<strong>${tz.timeZoneId}</strong>).<br><br>Deberás actualizar manualmente la zona horaria en los calendarios de Google de tus colaboradoras para que las citas se muestren correctamente.`,
                                                    confirmButtonColor: '#D4537E',
                                                });
                                            }
                                        });
                                    });
                                }
                            })
                            .catch(e => {
                                // Fallback mejorado con detección por estado para México
                                if (place.address_components) {
                                    const paisComp = place.address_components.find(c => c.types.includes('country'));
                                    const estadoComp = place.address_components.find(c => c.types.includes('administrative_area_level_1'));
                                    let zona = paisComp ? ZONAS_POR_PAIS[paisComp.short_name] : null;
                                    // Zonas especiales de México
                                    if (paisComp?.short_name === 'MX' && estadoComp) {
                                        const estado = estadoComp.long_name;
                                        if (/quintana roo/i.test(estado)) zona = 'America/Cancun';
                                        else if (/sonora/i.test(estado)) zona = 'America/Hermosillo';
                                        else if (/chihuahua|sinaloa|nayarit|durango/i.test(estado)) zona = 'America/Chihuahua';
                                        else if (/baja california$/i.test(estado)) zona = 'America/Tijuana';
                                        else if (/baja california sur/i.test(estado)) zona = 'America/Mazatlan';
                                        else zona = 'America/Mexico_City';
                                    }
                                    if (zona) dispatch(actualizarZonaHoraria(zona, getLadaPorZona(zona)));
                                }
                            });
                    }
                }
            });
        } catch (e) {
            console.log('Autocomplete error:', e);
        }
    }, []);

    const handleTelefono = (idx, valor) => {
        const soloNumeros = valor.replace(/\D/g, '').slice(0, 10);
        const nuevos = [...telefonos];
        nuevos[idx] = soloNumeros;
        setTelefonos(nuevos);
    };

    const agregarTelefono = () => { if (telefonos.length < 3) setTelefonos([...telefonos, '']); };
    const quitarTelefono = (idx) => setTelefonos(telefonos.filter((_, i) => i !== idx));

    const handleContinuar = async () => {
        const dirFinal = direccion.trim() || inputRef.current?.value?.trim();
        if (!dirFinal) {
            Swal.fire('Falta la dirección', 'Por favor busca o escribe la dirección de tu negocio.', 'warning');
            return;
        }
        const telesValidos = telefonos.filter(t => t.length === 10);
        if (telesValidos.length === 0) {
            Swal.fire('Falta el teléfono', 'Agrega al menos un teléfono de 10 dígitos.', 'warning');
            return;
        }
        setCargando(true);
        try {
            const tolVal = tolerancia ? (parseInt(minutos) || 10) : -1;
            await dispatch(guardarDireccion({
                direccion: dirFinal,
                maps: mapsLink || `https://www.google.com/maps/search/${encodeURIComponent(dirFinal)}`,
                telefonos: telesValidos.map(t => lada + t),
                tolerancia: tolVal,
            }));
            siguiente();
        } catch (e) {
            Swal.fire('Error', 'No se pudo guardar. Intenta de nuevo.', 'error');
        } finally {
            setCargando(false);
        }
    };

    return (
        <div className="app-container">
            <div className="app-header">
                <h1>Tu negocio</h1>
                <p>Paso 2 de 4</p>
            </div>
            <ProgressBar paso={2} />
            <div className="screen-body">
                <div className="field-label">Dirección de tu negocio</div>
                <input
                    ref={inputRef}
                    className="field-input"
                    placeholder="Busca tu negocio o escribe la dirección..."
                    value={direccion}
                    onChange={e => { setDireccion(e.target.value); setLugarSeleccionado(false); }}
                />
                <p className="field-hint">📍 Escribe y selecciona de la lista para confirmar en Maps</p>

                {lugarSeleccionado && (
                    <div className="info-box" style={{ marginTop: 10 }}>
                        <div className="info-box-title">✓ Ubicación confirmada en Maps</div>
                        <div className="info-box-body">{direccion}</div>
                    </div>
                )}

                <div className="field-label" style={{ marginTop: 20 }}>Teléfonos de contacto</div>
                <p className="field-hint" style={{ marginBottom: 8 }}>Tus clientas podrán llamarte o escribirte aquí</p>
                {telefonos.map((tel, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <div style={{ padding: '12px', border: '0.5px solid var(--border)', borderRadius: 10, fontSize: 14, color: 'var(--pink-900)', whiteSpace: 'nowrap', background: 'var(--pink-50)' }}>
                            {bandera} +{lada}
                        </div>
                        <input
                            className="field-input"
                            style={{ flex: 1 }}
                            placeholder="10 dígitos"
                            type="tel"
                            inputMode="numeric"
                            value={tel}
                            onChange={e => handleTelefono(idx, e.target.value)}
                            maxLength={10}
                        />
                        {idx > 0 && <button onClick={() => quitarTelefono(idx)} style={{ color: '#c62828', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>}
                    </div>
                ))}
                {telefonos.length < 3 && <button className="add-btn" onClick={agregarTelefono}>+ Agregar otro teléfono</button>}

                <div className="field-label" style={{ marginTop: 20 }}>¿Das tolerancia de llegada a tus clientas?</div>
                <div className="toggle-row">
                    <div>
                        <div className="toggle-label">Sí, doy tolerancia</div>
                        <div className="toggle-sub">Le avisamos al confirmar la cita</div>
                    </div>
                    <div className={`toggle ${tolerancia ? 'on' : ''}`} onClick={() => setTolerancia(!tolerancia)}>
                        <div className="toggle-thumb" />
                    </div>
                </div>
                {tolerancia && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                        <input
                            className="field-input"
                            type="number"
                            value={minutos}
                            onChange={e => setMinutos(e.target.value.replace(/\D/g, ''))}
                            style={{ width: 80 }}
                            min={1}
                            max={60}
                        />
                        <span style={{ fontSize: 14, color: 'var(--pink-800)' }}>minutos de tolerancia</span>
                    </div>
                )}
            </div>
            <div className="screen-footer">
                <button className="btn-primary" onClick={handleContinuar} disabled={cargando}>{cargando ? 'Guardando...' : 'Continuar'}</button>
                <button className="btn-secondary" onClick={anterior}>Volver</button>
            </div>
        </div>
    );
};
